import { ReactElement } from 'react';
import { ICard } from '@monofront/types';
import BankCard from '@monofront/design-system/visuals/Icons/Old/BankCard';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import { brands } from '@monofront/business/constants';
import { useTranslations } from 'next-intl';

interface ICardDetailsProps {
  paymentMethod: ICard;
  tags?: ReactElement<any>[];
  withoutBrands?: boolean;
}

const CardDetails: React.FC<ICardDetailsProps> = ({
  paymentMethod,
  tags = [],
  withoutBrands = false,
}) => {
  const tAccount = useTranslations('Account');

  return (
    <>
      <div className="card-details">
        <span className="brand-image">
          {brands.includes(paymentMethod.brand) ? (
            <ResponsiveImage
              src={`/images/checkout/${paymentMethod.brand}.svg`}
              alt="piggy bank"
              width={30}
              height={30}
            />
          ) : (
            <BankCard width={24} height={24} aria-hidden />
          )}
        </span>
        <div className="details">
          <div className="details-with-tag">
            <span className="numbers" data-cs-mask>
              {paymentMethod.brand === 'unknown' || withoutBrands
                ? tAccount.rich('cardDetailsUnknown', {
                    last4: paymentMethod.last4,
                    span: (children) => (
                      <span className="brand-name">{children}</span>
                    ),
                  })
                : tAccount.rich('cardDetails', {
                    brand: paymentMethod.brand.replace('CARD', '.'),
                    last4: paymentMethod.last4,
                    span: (children) => (
                      <span className="brand-name">{children}</span>
                    ),
                  })}
            </span>
            <div className="tags">{tags.map((Tag) => Tag)}</div>
          </div>
          <div className="expiration-date">
            <span>
              {tAccount('cardExpiration', {
                month: paymentMethod.exp_month.toString().padStart(2, '0'),
                year: paymentMethod.exp_year,
              })}
            </span>
          </div>
        </div>
      </div>
      <style jsx>
        {`
          .tags :global(span + span) {
            margin-left: var(--space-2);
          }

          .card-details {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .brand-name {
            text-transform: capitalize;
          }

          .brand-image {
            margin-right: var(--space-4);
          }

          .brand-image :global(img) {
            align-items: center;
            display: flex;
          }

          .details {
            display: flex;
            flex-direction: column;
            flex-wrap: wrap;
            flex: 1;
            line-height: 1.2;
          }

          .details-with-tag > .tags {
            display: inline-block;
          }

          .expiration-date {
            color: var(--clr-grey-1);
            font-weight: normal;
          }

          .error {
            color: var(--clr-danger);
          }

          .numbers,
          .expiration-date {
            margin-right: var(--space-2);
          }
        `}
      </style>
    </>
  );
};

export default CardDetails;
